import * as React from "react"
import { cloneElement, createContext, useContext } from "react"
import { node } from "prop-types"
import Bugsnag from "@bugsnag/js"
import BugsnagPluginReact from "@bugsnag/plugin-react"
import { MainError } from "./MainError"

export function start(configuration) {
  const client = Bugsnag.start({
    ...configuration,
    plugins: [new BugsnagPluginReact(React)],
  })
  return client
}

const __bugsnagClient = createContext(null)

/**
 * @returns {import('@bugsnag/js').Client} The Bugsnag client instance
 * @throws {Error} If hook is used outside of a Bugsnag Provider
 */
export function useBugsnagClient() {
  const client = useContext(__bugsnagClient)
  if (client === null) {
    throw new Error("useBugsnagClient must be used within a Bugsnag Provider.")
  }
  return client
}

const IGNORED_ERROR_MESSAGES = [
  'Could not load "util".',
  "null is not an object (evaluating 's.id')",
]
const hasMaskedUrl = (event) =>
  event.errors[0].stacktrace.some(function (frame) {
    return frame.file && frame.file.indexOf("webkit-masked-url") !== -1
  })
function onError(event) {
  if (IGNORED_ERROR_MESSAGES.includes(event.errors[0].errorMessage))
    return false
  if (hasMaskedUrl(event)) return false
}

export const bugsnagClient = Bugsnag.createClient({
  apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
  appVersion: import.meta.env.VITE_SOURCE_VERSION || "unknown_version",
  enabledReleaseStages: ["prototype", "staging", "production"],
  releaseStage: import.meta.env.VITE_ACTIVE_ENV,
  user: { id: "anonymous" },
  onError,
  plugins: [new BugsnagPluginReact(React)],
})
const BugsnagErrorBoundary = bugsnagClient.getPlugin("react")

BugsnagClientProvider.propTypes = {
  children: node.isRequired,
}
export function BugsnagClientProvider({ children }) {
  const { Provider } = __bugsnagClient

  return <Provider value={bugsnagClient}>{children}</Provider>
}

export function ErrorBoundary({ fallback, ...props }) {
  function RenderFallback({ error, info }) {
    return cloneElement(fallback, { error, info })
  }
  return <BugsnagErrorBoundary FallbackComponent={RenderFallback} {...props} />
}
ErrorBoundary.propTypes = {
  fallback: node.isRequired,
}

export function BugsnagWrapper({ children }) {
  const [key, setKey] = React.useState(0)

  function onRestart() {
    if (key === 0) {
      setKey(1)
    } else {
      window.location.reload()
    }
  }

  function onError(event) {
    event.addMetadata("app", { errorBoundary: "BugsnagWrapper" })
  }

  return (
    <BugsnagClientProvider>
      <ErrorBoundary
        key={key}
        fallback={<MainError onRestart={onRestart} onError={onError} />}
      >
        {children}
      </ErrorBoundary>
    </BugsnagClientProvider>
  )
}
BugsnagWrapper.propTypes = {
  children: node,
}
